import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { resendEmailConfirmation } from '@api/user'

/**
 * Custom hook that encapsulates message verification logic:
 * - Email verification error handling (API code 1004)
 * - External link error handling (API code 1006)
 * - Resend verification email flow
 *
 * Shared between Conversation and CommunityMessageUser components.
 */
const useMessageVerification = () => {
    const [emailVerificationError, setEmailVerificationError] = useState(false)
    const [externalLinkError, setExternalLinkError] = useState(false)
    const [blockedError, setBlockedError] = useState(false)
    const [verificationEmailSent, setVerificationEmailSent] = useState(false)
    const [sendingVerificationEmail, setSendingVerificationEmail] = useState(false)

    const clearErrors = () => {
        setEmailVerificationError(false)
        setExternalLinkError(false)
        setBlockedError(false)
    }

    const resetAll = () => {
        clearErrors()
        setVerificationEmailSent(false)
        setSendingVerificationEmail(false)
    }

    const handleSendError = (error) => {
        if (error.response?.status === 403 && error.response?.data?.code === 1004) {
            setEmailVerificationError(true)
        } else if (error.response?.status === 403 && error.response?.data?.code === 1006) {
            setExternalLinkError(true)
        } else if (error.response?.status === 403) {
            setBlockedError(true)
        }
    }

    const handleResendVerificationEmail = () => {
        setSendingVerificationEmail(true)
        resendEmailConfirmation()
            .then(() => {
                setVerificationEmailSent(true)
            })
            .catch((error) => {
                Sentry.captureException(error)
            })
            .finally(() => {
                setSendingVerificationEmail(false)
            })
    }

    return {
        emailVerificationError,
        externalLinkError,
        blockedError,
        verificationEmailSent,
        sendingVerificationEmail,
        clearErrors,
        resetAll,
        handleSendError,
        handleResendVerificationEmail,
    }
}

export default useMessageVerification
